import { useAuthContext } from "providers/Auth";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  PlazaIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Plus } from "untitledui-js-base";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { AddAppfolioPropertyModal } from "shared/app/AppfolioPropertiesModal";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { useEligibleContactFormSources } from "shared/components/ContactFormsSubmenu/useEligibleContactFormSources";
import { useMemo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { ContactFormsSubmenu } from "shared/components/ContactFormsSubmenu";

type SettingsPayload = {
  settings?: {
    name: string;
    readonly: boolean;
    value: string | null | boolean;
    title: string;
  }[];
};

const AddPropertyButton = ({
  onPropertyAdded,
  isAppfolioWithReports
}: {
  onPropertyAdded?: () => void;
  isAppfolioWithReports: boolean;
}) => {
  const [isAppfolioPropertiesModalOpen, setAppfolioPropertiesModalOpen] =
    useState(false);

  const dictionary = useUnitsPropertiesDictionary();
  const navigate = useNavigate();

  const handleModalSuccess = () => {
    // This will be called when a property is successfully added
    if (onPropertyAdded) onPropertyAdded();
  };

  if (!isAppfolioWithReports)
    return (
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={() => navigate("/properties/add")}
        Icon={Plus}
        className="w-full"
      >
        Add {dictionary.property}
      </Button>
    );

  return (
    <>
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={() => {
          setAppfolioPropertiesModalOpen(true);
        }}
        Icon={Plus}
        className="w-full"
      >
        Appfolio {dictionary.properties}
      </Button>
      <AddAppfolioPropertyModal
        opened={isAppfolioPropertiesModalOpen}
        onClose={() => {
          setAppfolioPropertiesModalOpen(false);
        }}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

const CopyLinkControl = ({
  isBoomCRM,
  hasEligibleSources,
  partnerListingUrl,
  partnerApplicationLink,
  handleCopyLink
}: {
  isBoomCRM: boolean;
  hasEligibleSources: boolean;
  partnerListingUrl: string;
  partnerApplicationLink: string;
  handleCopyLink: (link: string) => void;
}) => {
  if (isBoomCRM && hasEligibleSources)
    return (
      <ContextMenuPopover
        placement="bottom-end"
        cbTrigger={(open) => (
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            HugeIcon={open ? ArrowUp01Icon : ArrowDown01Icon}
            iconPosition="right"
            className="w-full"
          >
            Copy link
          </Button>
        )}
        actions={[
          {
            title: (
              <div className="flex items-center gap-3">
                <HugeiconsIcon
                  size={20}
                  icon={PlazaIcon}
                  className="text-content-200"
                />
                <p className="text-base font-medium text-content-1000">
                  Copy listings link
                </p>
              </div>
            ),
            onClick: () => handleCopyLink(partnerListingUrl)
          },
          {
            title: (
              <ContactFormsSubmenu variant="FULL" onCopyLink={handleCopyLink} />
            ),
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            }
          }
        ]}
      />
    );

  if (isBoomCRM)
    return (
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        onClick={() => handleCopyLink(partnerListingUrl)}
        className="w-full"
      >
        Copy listings link
      </Button>
    );

  return (
    <Button
      variant={ButtonVariant.BASE}
      size={ButtonSize.SM}
      onClick={() => handleCopyLink(partnerApplicationLink)}
      className="w-full"
    >
      Copy portfolio link
    </Button>
  );
};

export const LayoutButtons = ({
  onPropertyAdded
}: {
  onPropertyAdded?: () => void;
}) => {
  const { partner, pms, type, permissions } = useAuthContext();
  const { eligibleSources } = useEligibleContactFormSources();
  const hasEligibleSources = eligibleSources.length > 0;

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Fetch settings to check if Appfolio reports are enabled
  const { data: settingsData } = useQuery<SettingsPayload>({
    queryKey: ["SETTINGS"],
    queryFn: () => instance.get(`/portal/v1/settings`).then(({ data }) => data)
  });

  const isAppfolioWithReports = useMemo(() => {
    const value = settingsData?.settings?.find(
      (setting) => setting.name === "appfolio_use_reports"
    )?.value;

    const isEnabled = value === "true" || value === true;

    return Boolean(pms.isAppfolio && isEnabled);
  }, [pms.isAppfolio, settingsData]);

  if (type.isBoomScreen) {
    const canShowCopyLink = permissions.canManageAllProperties;
    const canShowAddProperty =
      permissions.canManageProperties ||
      (pms.isAppfolio && isAppfolioWithReports);

    if (!canShowCopyLink && !canShowAddProperty)
      return (
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={() =>
            handleCopyLink(partner?.magic_link?.application_link ?? "")
          }
        >
          Copy portfolio link
        </Button>
      );

    return (
      <>
        <Show if={canShowCopyLink}>
          <CopyLinkControl
            isBoomCRM={type.isBoomCRM}
            hasEligibleSources={hasEligibleSources}
            partnerListingUrl={partner?.listing_url ?? ""}
            partnerApplicationLink={partner?.magic_link?.application_link ?? ""}
            handleCopyLink={handleCopyLink}
          />
        </Show>
        <Show if={canShowAddProperty}>
          <AddPropertyButton
            onPropertyAdded={onPropertyAdded}
            isAppfolioWithReports={isAppfolioWithReports}
          />
        </Show>
      </>
    );
  }

  if (permissions.canManageProperties)
    return (
      <AddPropertyButton
        onPropertyAdded={onPropertyAdded}
        isAppfolioWithReports={false}
      />
    );

  return null;
};
